<template>
  <TemplateResourceVideo
    v-if="isVideoTemplate"
    v-bind="data"
  />
  <TemplateResourceEbook
    v-else
    v-bind="data"
  />
</template>

<script setup>
// Meta
definePageMeta({
  layout: 'empty',
  middleware: [
    'check-if-the-resource-exists',
  ],
})

// i18n
defineI18nRoute({
  locales: ['de', 'en', 'fr'],
  paths: {
    de: '/ressourcen/[type]/[slug]',
    en: '/resources/[type]/[slug]',
    fr: '/ressources/[type]/[slug]',
  },
})

// Composables
const { state } = useConditionalPreviewMode()
const { locale } = useI18n()
const localePath = useLocalePath()
const { $slugify } = useNuxtApp()
const { meta, params } = useRoute()
const getRouteBaseName = useRouteBaseName()
const { public: publicRuntimeConfig } = useRuntimeConfig()

if (['es'].includes(locale.value)) {
  throw createError({ statusCode: 404, message: 'Page not found' })
}

// Async Data
const { data } = await useAsyncData(
  `page-resources-[type]-[slug]-${params.slug}-${locale.value}`,
  async () => {
    return Promise.all([
      $fetch(`/api/content/pages/resources-index/${locale.value}`, { query: { preview: state.preview } }),
      $fetch(`/api/content/pages/resources-list/${locale.value}`, { query: { preview: state.preview } }),
    ])
  },
  {
    transform: ([{ componentDiscoverTheOffer, componentForm, componentGetStarted, componentGlobal, componentHowToAddAVideo, componentOfferForm, componentRelatedContent, componentVirtualBackgrounds, home, page: hub }, { allModelOffers }]) => {
      const page = allModelOffers.find(({ slug }) => slug === params.slug)
      return {
        componentDiscoverTheOffer,
        componentForm,
        componentGetStarted,
        componentGlobal,
        componentHowToAddAVideo,
        componentOfferForm,
        componentRelatedContent,
        componentVirtualBackgrounds,
        home,
        hub,
        page,
      }
    },
  },
)

if (!data.value?.page) {
  throw createError({ statusCode: 404, message: 'Page not found' })
}

if (data.value.page.isAVideoTemplate) {
  if (!data.value.page.videoFile) {
    throw createError({ statusCode: 404, message: 'Page not found' })
  }
}

// Computed Properties
const isVideoTemplate = computed(() => {
  return !!data.value.page.isAVideoTemplate
})

// Meta
const { alternativeVersions } = data.value.page
const locales = alternativeVersions.map(({ locale }) => locale)

meta.nuxtI18n = alternativeVersions.reduce(
  (accumator, { locale, value: slug }) => ({
    ...accumator,
    [locale]: {
      slug,
      type: $slugify(
        data.value.page.resourceType._allNameLocales.find(({ locale: lang }) => lang === locale).value,
      ),
    },
  }),
  {},
)

const head = useLocaleHead({
  identifierAttribute: 'id',
  addSeoAttributes: true,
})

useHead({
  link: head.value.link
    .filter((link) => {
      return link.rel === 'canonical'
        || (link.rel === 'alternate' && link.hreflang === 'x-default')
        || (link.rel === 'alternate' && locales.includes(link.hreflang))
    })
    .map((link) => {
      return {
        ...link,
        href: !locales.includes('en') && link.id === 'i18n-xd'
          ? `${publicRuntimeConfig.baseUrl}${localePath({ name: getRouteBaseName() })}`
          : link.href,
      }
    }),
  meta: head.value.meta.filter((meta) => {
    return meta.property === 'og:url'
      || meta.property === 'og:locale'
      || (meta.property === 'og:locale:alternate' && locales.includes(meta.content))
  }),
  script: [
    {
      innerHTML: JSON.stringify({
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        'itemListElement': [
          {
            '@type': 'ListItem',
            'position': 1,
            'name': data.value.home.trackingBreadcrumbTitle,
            'item': `${publicRuntimeConfig.baseUrl}${localePath('index')}`,
          },
          {
            '@type': 'ListItem',
            'position': 2,
            'name': data.value.hub.trackingBreadcrumbTitle,
            'item': `${publicRuntimeConfig.baseUrl}${localePath('resources')}`,
          },
          {
            '@type': 'ListItem',
            'position': 3,
            'name': data.value.page.trackingBreadcrumbTitle || data.value.page.pageTitle,
          },
        ],
      }),
      type: 'application/ld+json',
    },
  ],
})

useSeoMeta({
  title: data.value.page.pageTitleSeo,
  description: data.value.page.shortDescription,
  ogTitle: data.value.page.pageTitleSeo,
  ogDescription: data.value.page.shortDescription,
  twitterTitle: data.value.page.pageTitleSeo,
  twitterDescription: data.value.page.shortDescription,
})
</script>
